import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp, confirmSignUp, autoSignIn, getCurrentUser } from 'aws-amplify/auth';
import { useUserStore } from '@/stores/userStore';

export function SignUpPage() {
  const [step, setStep] = useState<'signup' | 'confirm'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const enableBackend = useUserStore((state) => state.enableBackend);
  const setProfileId = useUserStore((state) => state.setProfileId);
  const fetchProfile = useUserStore((state) => state.fetchProfile);

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            preferred_username: username || email.split('@')[0],
          },
        },
      });

      // Check if user is already confirmed (auto-confirm enabled in Cognito)
      if (result.isSignUpComplete) {
        // User is auto-confirmed, sign them in directly
        try {
          await autoSignIn();
        } catch (autoSignInError) {
          // If autoSignIn fails, fall back to manual sign in
          console.warn('AutoSignIn failed, using manual sign in:', autoSignInError);
          const { signIn } = await import('aws-amplify/auth');
          await signIn({ username: email, password });
        }

        // Get the authenticated user's ID
        const user = await getCurrentUser();

        // Use the username from signup form or extract from email
        const displayUsername = username || email.split('@')[0];

        // Update profile to use Cognito user ID and username
        setProfileId(user.userId, displayUsername);

        // Enable backend sync
        enableBackend();

        // Fetch the user's profile from backend to get correct XP and level
        await fetchProfile();

        // Redirect to home
        navigate('/');
      } else {
        // User needs to confirm via email code
        setStep('confirm');
      }
    } catch (err: any) {
      console.error('Sign up error:', err);
      if (err.name === 'UsernameExistsException') {
        setError('An account with this email already exists');
      } else if (err.name === 'InvalidPasswordException') {
        setError('Password must contain uppercase, lowercase, and numbers');
      } else {
        setError(err.message || 'Failed to sign up');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await confirmSignUp({
        username: email,
        confirmationCode,
      });

      // Auto sign in after confirmation
      try {
        await autoSignIn();
      } catch (autoSignInError) {
        // If autoSignIn fails, fall back to manual sign in
        console.warn('AutoSignIn failed, using manual sign in:', autoSignInError);
        const { signIn } = await import('aws-amplify/auth');
        await signIn({ username: email, password });
      }

      // Get the authenticated user's ID
      const user = await getCurrentUser();

      // Use the username from signup form or extract from email
      const displayUsername = username || email.split('@')[0];

      // Update profile to use Cognito user ID and username
      setProfileId(user.userId, displayUsername);

      // Enable backend sync
      enableBackend();

      // Fetch the user's profile from backend to get correct XP and level
      await fetchProfile();

      // Redirect to home
      navigate('/');
    } catch (err: any) {
      console.error('Confirmation error:', err);
      if (err.name === 'CodeMismatchException') {
        setError('Invalid confirmation code');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Confirmation code has expired. Please request a new one.');
      } else {
        setError(err.message || 'Failed to confirm account');
      }
    } finally {
      setIsLoading(false);
    }
  }

  if (step === 'confirm') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <div className="max-w-md w-full bg-black border border-primary-500/30 rounded-lg shadow-[0_0_30px_rgba(34,197,94,0.2)] p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-primary-400 mb-2 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]">
              Confirm Your Email
            </h1>
            <p className="text-primary-200/70">
              We sent a confirmation code to <strong className="text-primary-300">{email}</strong>
            </p>
          </div>

          <form onSubmit={handleConfirm} className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-primary-300 mb-2">
                Confirmation Code
              </label>
              <input
                id="code"
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="w-full px-4 py-3 bg-black border border-primary-500/30 rounded-lg text-primary-200 text-center text-2xl tracking-widest placeholder-primary-200/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all"
                placeholder="000000"
                required
                maxLength={6}
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-500 text-black py-3 rounded-lg font-semibold hover:bg-primary-600 hover:drop-shadow-[0_0_20px_rgba(34,197,94,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Confirming...' : 'Confirm Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setStep('signup')}
              className="text-primary-400 hover:text-primary-300 text-sm hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.5)] transition-all"
            >
              Back to sign up
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4">
      <div className="max-w-md w-full bg-black border border-primary-500/30 rounded-lg shadow-[0_0_30px_rgba(34,197,94,0.2)] p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-400 mb-2 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]">
            Create Account
          </h1>
          <p className="text-primary-200/70">Join CodeQuest and start learning</p>
        </div>

        <form onSubmit={handleSignUp} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-primary-300 mb-2">
              Username (optional)
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-primary-500/30 rounded-lg text-primary-200 placeholder-primary-200/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all"
              placeholder="awesome_coder"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-primary-300 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-primary-500/30 rounded-lg text-primary-200 placeholder-primary-200/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-primary-300 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-primary-500/30 rounded-lg text-primary-200 placeholder-primary-200/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all"
              placeholder="••••••••"
              required
            />
            <p className="text-xs text-primary-200/50 mt-1">
              Must be 8+ characters with uppercase, lowercase, and numbers
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-primary-300 mb-2">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-primary-500/30 rounded-lg text-primary-200 placeholder-primary-200/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/50 transition-all"
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary-500 text-black py-3 rounded-lg font-semibold hover:bg-primary-600 hover:drop-shadow-[0_0_20px_rgba(34,197,94,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-primary-200/70">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.5)] transition-all">
              Sign in
            </Link>
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-primary-500/20 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-primary-200/60 hover:text-primary-400 text-sm transition-all"
          >
            Continue without account (local mode)
          </button>
        </div>
      </div>
    </div>
  );
}
